import React, {useEffect, useMemo, useState} from "react";
import {
    Box, Button, Card, CardContent, FormControl,
    IconButton, InputLabel, MenuItem, Select,
    Stack, TextField, Tooltip, Typography, Paper,
    Alert, CircularProgress
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import TuneIcon from "@mui/icons-material/Tune";
import CalculateIcon from "@mui/icons-material/Calculate";
import {createScript, getScriptById, updateScript, countEvalResults} from "../api/AiAgentApi";
import TemplateBodyBuilder from "./TemplateBodyBuilder";
import {useNavigate, useParams} from "react-router-dom";

const AMO_CRM_STATUS = [
    {
        id: 76732506,
        name: "Неразобранное"
    },
    {
        id: 76732510,
        name: "Новая заявка"
    },
    {
        id: 76732514,
        name: "В обработке"
    },
    {
        id: 76861810,
        name: "Лист ожидания"
    },
    {
        id: 76732518,
        name: "Забронировано"
    },
    {
        id: 76861814,
        name: "Завтра"
    },
    {
        id: 76732522,
        name: "Сегодня"
    },
    {
        id: 142,
        name: "Пришел - Успешно реализовано"
    },
    {
        id: 143,
        name: "Закрыто и не реализовано"
    },
    {
        id: -1,
        name: "Без броней"
    }
];

const RESOLVER_PATH_OPTIONS = [
    {
        value: "context_user_id",
        label: "ID пользователя"
    },
    {
        value: "context_telegram_id",
        label: "Telegram ID пользователя"
    },
    {
        value: "context_vk_id",
        label: "VK ID пользователя"
    },
    {
        value: "context_minutes_since_last_message",
        label: "Минут с последнего сообщения"
    },
    {
        value: "context_total_reservations",
        label: "Количество брони"
    },
    {
        value: "context_amocrm_last_reservation_status",
        label: "Статус последнего бронирования в amoCRM"
    }
];

const CMP_OPS = ["==", ">", "<"];
const TOP_LOGIC = [
    {
        value: "and",
        label: "И"
    },
    {
        value: "or",
        label: "ИЛИ"
    }
];

// ===== Утилиты =====
const rid = () => Math.random().toString(36).slice(2, 10);
const isBlank = (s) => !s || String(s).trim().length === 0;

function validateJsonString(s) {
    try {
        const obj = JSON.parse(s);
        return {ok: true, obj};
    } catch (e) {
        return {ok: false, error: e.message};
    }
}

// Сериализация дерева в JSONLogic
function toJsonLogic(node) {
    if (!node) return {};
    if (node.kind === "rule") {
        const left = {var: node.left.name};
        let right = null;
        if (node.left.source === 'EXTERNAL_INTERNAL') {
            right = {var: node.left.name + "_in"};
        } else {
            if (node.rightType === "var") right = {var: node.right};
            else if (node.rightType === "value") right = node.right; // строка
            else if (node.rightType === "null") right = null;
        }
        return {[node.op]: [left, right]};
    }
    // group
    const children = (node.children || []).map(toJsonLogic).filter(n => Object.keys(n || {}).length > 0);
    if (children.length === 0) return {};
    if (children.length === 1) return children[0];
    return {[node.combinator || "and"]: children};
}

// ====== Рекурсивный рендер узла Builder'а ======
function BuilderNode({
                         node,
                         params,
                         onChange,
                         onRemove,
                         onCreateParam,
                         isRoot = false,
                     }) {
    if (node.kind === "rule") {
        return (
            <Stack
                direction={{xs: "column", md: "row"}}
                spacing={1}
                alignItems="center"
                sx={{p: 1, borderRadius: 1, border: "1px dashed rgba(255,255,255,0.2)"}}
            >
                <FormControl size="small" sx={{minWidth: 160}}>
                    <InputLabel>Для кого должно быть выполнено?</InputLabel>
                    <Select
                        label="Для кого должно быть выполнено?"
                        value={node.left?.resolverPath || ""}
                        onChange={(e) => {
                            const selectedValue = e.target.value;
                            const param = {
                                id: rid(),
                                name: rid(),
                                type: "STRING",
                                source: "EXTERNAL_INTERNAL_VALUE",
                                required: true,
                                defaultValue: "",
                                resolverPath: selectedValue
                            };
                            onCreateParam(param);
                            onChange({...node, left: param});
                        }}
                    >
                        {RESOLVER_PATH_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{minWidth: 100}}>
                    <InputLabel>оператор</InputLabel>
                    <Select
                        label="оператор"
                        value={node.op}
                        onChange={(e) => onChange({...node, op: e.target.value})}
                    >
                        {CMP_OPS.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                    </Select>
                </FormControl>

                {(node.left && node.left.source !== "EXTERNAL_INTERNAL") &&
                    <>
                        {(node.rightType === "value" && node.left.resolverPath === "context_amocrm_last_reservation_status") ? <>
                            <FormControl size="small" sx={{minWidth: 160}}>
                                <InputLabel>Amo Crm Status</InputLabel>
                                <Select
                                    label="Amo Crm Status"
                                    value={node.right || ""}
                                    onChange={(e) => onChange({...node, right: e.target.value})}
                                >
                                    {AMO_CRM_STATUS.map(p => <MenuItem key={p.id}
                                                                       value={p.id}>{p.name || "(no name)"}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </> : node.rightType === "value" ? <>
                            <TextField
                                size="small"
                                label="значение"
                                value={node.right || ""}
                                onChange={(e) => onChange({...node, right: e.target.value})}
                            />
                        </> : <></>
                        }
                        {node.rightType === "var" && (
                            <FormControl size="small" sx={{minWidth: 160}}>
                                <InputLabel>правый параметр</InputLabel>
                                <Select
                                    label="правый параметр"
                                    value={node.right || ""}
                                    onChange={(e) => onChange({...node, right: e.target.value})}
                                >
                                    {params.map(p => <MenuItem key={p.id}
                                                               value={p.name}>{p.name || "(no name)"}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    </>
                }

                {!isRoot && (
                    <Tooltip title="Удалить правило">
                        <IconButton onClick={onRemove}><DeleteOutlineIcon/></IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    }

    // Group
    return (
        <Paper variant="outlined" sx={{p: 1.25, borderRadius: 2, borderStyle: "dashed"}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                <FolderOpenIcon fontSize="small"/>
                <Typography variant="subtitle2">Группа</Typography>
                <FormControl size="small" sx={{ml: "auto", minWidth: 135}}>
                    <InputLabel>Комбинатор</InputLabel>
                    <Select
                        label="Комбинация условий"
                        value={node.combinator || ""}
                        onChange={(e) => onChange({...node, combinator: e.target.value})}
                        fullWidth
                        size="small"
                    >
                        {TOP_LOGIC.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {!isRoot && (
                    <Tooltip title="Удалить группу">
                        <IconButton onClick={onRemove} color="error"><DeleteOutlineIcon/></IconButton>
                    </Tooltip>
                )}
            </Stack>

            <Stack spacing={1}>
                {node.children?.map((child, idx) => (
                    <BuilderNode
                        key={child.id}
                        node={child}
                        params={params}
                        isRoot={false}
                        onChange={(updatedChild) => {
                            const nextChildren = [...node.children];
                            nextChildren[idx] = updatedChild;
                            onChange({...node, children: nextChildren});
                        }}
                        onRemove={() => {
                            const nextChildren = node.children.filter((_, i) => i !== idx);
                            onChange({...node, children: nextChildren});
                        }}
                        onCreateParam={onCreateParam}
                    />
                ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{mt: 1}}>
                <Button
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={() => {
                        const rule = {id: rid(), kind: "rule", left: "", op: "==", rightType: "value", right: ""};
                        onChange({...node, children: [...(node.children || []), rule]});
                    }}
                >
                    Добавить правило
                </Button>
                <Button
                    size="small"
                    startIcon={<TuneIcon/>}
                    onClick={() => {
                        const group = {id: rid(), kind: "group", combinator: "and", children: []};
                        onChange({...node, children: [...(node.children || []), group]});
                    }}
                >
                    Добавить группу
                </Button>
            </Stack>
        </Paper>
    );
}

// ===== Основной компонент =====
export default function CreateScriptForm({onNext, requestScriptBody}) {
    const navigate = useNavigate();
    const [title, setTitle] = useState(rid());
    const [templateBody, setTemplateBody] = useState("");
    const [conditionMode, setConditionMode] = useState("builder");
    const [rawCondition, setRawCondition] = useState("");

    const {scriptId} = useParams();
    const [isLoadingScript, setIsLoadingScript] = useState(true);

    // Параметры скрипта
    const [params, setParams] = useState([]);

    // Корневой Builder
    const [root, setRoot] = useState({});
    const [errors, setErrors] = useState({});

    // Состояния для подсчета пользователей
    const [countLoading, setCountLoading] = useState(false);
    const [userCount, setUserCount] = useState(null);
    const [countError, setCountError] = useState(null);

    const conditionPreview = useMemo(() => {
        if (conditionMode === "builder") {
            const jsonObj = toJsonLogic(root);
            return JSON.stringify(jsonObj, null, 2);
        }
        return rawCondition;
    }, [conditionMode, root, rawCondition]);

    // Функция для подготовки параметров в формате запроса
    const prepareParamsForRequest = () => {
        return params.flatMap(p => {
            if (p.source === 'EXTERNAL_INTERNAL') {
                const base = {
                    required: !!p.required,
                    defaultValue: isBlank(p.defaultValue) ? null : p.defaultValue,
                };
                return [
                    {
                        ...base,
                        name: p.name.trim(),
                        source: 'EXTERNAL',
                        resolverPath: null,
                    },
                    {
                        ...base,
                        name: p.name.trim() + "_in",
                        source: 'INTERNAL',
                        resolverPath: p.resolverPath,
                    }
                ];
            }

            if (p.source === "EXTERNAL_INTERNAL_VALUE") {
                return {
                    name: p.name.trim(),
                    type: p.type,
                    source: "INTERNAL",
                    required: !!p.required,
                    defaultValue: isBlank(p.defaultValue) ? null : p.defaultValue,
                    resolverPath: p.resolverPath,
                };
            }

            return {
                name: p.name.trim(),
                type: p.type,
                source: p.source,
                required: !!p.required,
                defaultValue: isBlank(p.defaultValue) ? null : p.defaultValue,
                resolverPath: p.source === "INTERNAL" ? p.resolverPath : null,
            };
        });
    };

    // Функция для подсчета пользователей
    const handleCountUsers = async () => {
        if (!conditionPreview || conditionPreview.trim() === "") {
            setCountError("Сначала заполните условия");
            return;
        }

        setCountLoading(true);
        setCountError(null);
        setUserCount(null);

        try {
            // Подготавливаем параметры
            const preparedParams = prepareParamsForRequest();

            // Находим EXTERNAL параметры для deferredAction
            const externalParams = preparedParams.filter(p => p.source === "EXTERNAL");

            // Формируем payload
            const payload = {
                condition: conditionPreview,
                deferredAction: {
                    name: `temp_action_${Date.now()}`,
                    scriptId: 0, // Временный ID
                    intervalMinutes: 60,
                    params: externalParams.map(p => ({
                        value: p.defaultValue || "",
                        scriptParamId: 0,
                        name: p.name
                    }))
                },
                script: {
                    title: title.trim() || "Без названия",
                    templateBody: templateBody || "",
                    condition: conditionPreview,
                    params: preparedParams
                }
            };

            console.log("Отправляемый payload:", payload);
            const count = await countEvalResults(payload);
            setUserCount(count);
        } catch (error) {
            console.error("Ошибка при подсчете пользователей:", error);
            setCountError(error.message || "Произошла ошибка при подсчете");
        } finally {
            setCountLoading(false);
        }
    };

    function addParamRow() {
        setParams(prev => [
            ...prev,
            {
                id: rid(),
                name: "",
                type: "STRING",
                source: "EXTERNAL_INTERNAL_VALUE",
                required: true,
                defaultValue: "",
                resolverPath: "context_user_id"
            }
        ]);
    }

    function updateParam(id, patch) {
        setParams(prev => prev.map(p => p.id === id ? {...p, ...patch} : p));
    }

    function removeParam(id) {
        setParams(prev => prev.filter(p => p.id !== id));
    }

    function validate() {
        const e = {};

        const names = params.map(p => (p.name || "").trim()).filter(Boolean);
        const dups = names.filter((n, i) => names.indexOf(n) !== i);
        if (dups.length) e.params = "Имена параметров должны быть уникальны: " + Array.from(new Set(dups)).join(", ");

        const wrongInternal = params.filter(p => p.source === "INTERNAL" && isBlank(p.resolverPath));
        if (wrongInternal.length) e.params = (e.params ? e.params + " | " : "") + "INTERNAL параметры должны иметь resolverPath";

        if (conditionMode === "raw") {
            const res = validateJsonString(rawCondition);
            if (!res.ok) e.condition = "Некорректный JSON: " + res.error;
        } else {
            const preview = toJsonLogic(root);
            if (!preview || Object.keys(preview).length === 0) {
                e.condition = "Заполните хотя бы одно правило/группу";
            }
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function onSubmit() {
        if (!validate()) return;

        const request = {
            title: title.trim(),
            templateBody,
            condition: conditionPreview,
            params: prepareParamsForRequest(),
        };

        if (onNext) {
            onNext(request);
        } else if (scriptId) {
            await updateScript(scriptId, request);
            navigate('/deferred-actions');
        } else {
            await createScript(request);
            navigate('/deferred-actions');
        }
    }

    function setScript(res) {
        setTitle(res.title);
        setTemplateBody(res.templateBody);

        const baseParams = Array.isArray(res.params) ? res.params : [];
        setParams(baseParams);

        let parsed;
        try {
            parsed = res.condition ? JSON.parse(res.condition) : {};
        } catch {
            parsed = {};
        }

        const {node, extraParams} = fromJsonLogic(parsed, baseParams);

        const safeRoot =
            node && node.kind
                ? node
                : {id: "root", kind: "group", combinator: "and", children: []};

        setRoot(safeRoot);

        if (extraParams.length) {
            setParams(prev => [...prev, ...extraParams]);
        }

        setRawCondition(res.condition || "");
        setIsLoadingScript(false);
    }

    useEffect(() => {
        if (scriptId) {
            getScriptById(scriptId)
                .then((res) => {
                    setScript(res);
                })
                .catch(() => setIsLoadingScript(false));
        } else {
            setIsLoadingScript(false);
        }
    }, [scriptId]);

    useEffect(() => {
        if (requestScriptBody) {
            setScript(requestScriptBody);
        }
    }, [requestScriptBody]);

    return (
        <Box>
            <Card sx={{mb: 2}}>
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 1}}>
                        <Typography variant="subtitle1">Для кого должно быть выполнено?</Typography>
                    </Stack>

                    {errors.condition && (
                        <Typography color="error" variant="body2" sx={{mb: 1}}>{errors.condition}</Typography>
                    )}

                    {!isLoadingScript && <>
                        {conditionMode === "raw" ? (
                            <TextField
                                label="Condition JSON (JSONLogic)"
                                value={rawCondition}
                                onChange={(e) => setRawCondition(e.target.value)}
                                fullWidth
                                multiline
                                minRows={10}
                            />
                        ) : (
                            <Stack spacing={1}>
                                <BuilderNode
                                    node={root}
                                    params={params}
                                    isRoot
                                    onChange={setRoot}
                                    onRemove={() => {}}
                                    onCreateParam={(p) => {
                                        setParams(prev => [...prev, p]);
                                    }}
                                />
                            </Stack>
                        )}
                    </>
                    }
                </CardContent>
            </Card>

            <Card>
                {!isLoadingScript &&
                    <TemplateBodyBuilder
                        params={params}
                        value={templateBody}
                        onChange={setTemplateBody}
                        onParamsChange={setParams}
                    />
                }
            </Card>

            <div style={{height: "10px"}}/>

            {/* Блок подсчета пользователей */}
            <Card sx={{mb: 2}}>
                <CardContent>
                    <Stack spacing={2}>
                        <Button
                            variant="outlined"
                            onClick={handleCountUsers}
                            disabled={countLoading || conditionMode === "builder" && (!root || !root.children || root.children.length === 0)}
                            startIcon={countLoading ? <CircularProgress size={20} /> : <CalculateIcon />}
                            fullWidth
                        >
                            {countLoading ? "Подсчет..." : "Подсчитать количество пользователей под условия"}
                        </Button>

                        {userCount !== null && (
                            <Alert severity="info">
                                <Typography variant="body2">
                                    Количество пользователей, которые попадут под условия: <strong>{userCount}</strong>
                                </Typography>
                            </Alert>
                        )}

                        {countError && (
                            <Alert severity="error">
                                {countError}
                            </Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="contained" onClick={onSubmit}>
                    {scriptId ? "Обновить" : "Далее"}
                </Button>
            </Stack>
        </Box>
    );
}

// === JSONLogic -> Builder ===
function fromJsonLogic(logicObj, existingParams = []) {
    const byName = new Map((existingParams || []).map(p => [p.name, p]));
    const extraParams = [];

    const ensureParam = (name) => {
        if (!name) return null;
        let p = byName.get(name);
        if (!p) {
            p = {
                id: rid(),
                name,
                type: "STRING",
                source: "EXTERNAL",
                required: false,
                defaultValue: "",
                resolverPath: ""
            };
            byName.set(name, p);
            extraParams.push(p);
        }
        return p;
    };

    const OPS = new Set(["==", "!=", ">", ">=", "<", "<="]);
    const LOGICS = new Set(["and", "or"]);

    function parseNode(node) {
        if (!node || typeof node !== "object" || Array.isArray(node)) {
            return {id: rid(), kind: "group", combinator: "and", children: []};
        }

        const keys = Object.keys(node);
        if (keys.length !== 1) {
            const children = keys.map(k => parseNode({[k]: node[k]}));
            return {id: rid(), kind: "group", combinator: "and", children};
        }

        const key = keys[0];
        const val = node[key];

        if (LOGICS.has(key)) {
            const arr = Array.isArray(val) ? val : [];
            const children = arr.map(parseNode).filter(Boolean);
            return {id: rid(), kind: "group", combinator: key, children};
        }

        if (OPS.has(key)) {
            const arr = Array.isArray(val) ? val : [];
            const leftSpec = arr[0];
            const rightSpec = arr.length > 1 ? arr[1] : undefined;

            const leftName = leftSpec && typeof leftSpec === "object" && leftSpec.var ? leftSpec.var : "";
            const leftParam = ensureParam(leftName);

            let rightType = "value";
            let right = "";

            if (rightSpec === null) {
                rightType = "null";
                right = null;
            } else if (rightSpec && typeof rightSpec === "object" && rightSpec.var) {
                rightType = "var";
                right = rightSpec.var;
                ensureParam(right);
            } else if (typeof rightSpec === "undefined") {
                rightType = "value";
                right = "";
            } else {
                rightType = "value";
                right = String(rightSpec);
            }

            return {
                id: rid(),
                kind: "rule",
                left: leftParam,
                op: key,
                rightType,
                right
            };
        }

        const fallbackChildren = Array.isArray(val) ? val : [val];
        const children = fallbackChildren.map(parseNode);
        return {id: rid(), kind: "group", combinator: "and", children};
    }

    const node = parseNode(logicObj);
    return {node, extraParams};
}